import { CdkCopyToClipboard, Clipboard } from '@angular/cdk/clipboard';
import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject,
  input,
  output,
} from '@angular/core';
import { ResponsiveService } from '@cf/core';
import { CfIconDirective } from '@cf/shared/ui/icons';
import { TranslateService } from '@tolgee/ngx';
import { toast } from 'ngx-sonner';
import { NgxSvgSpriteFragment } from 'ngxtension/svg-sprite';

@Component({
  selector: 'cf-clipboard-button',
  template: `
    <svg
      [cdkCopyToClipboard]="link"
      (cdkCopyToClipboardCopied)="onCopy()"
      [cfIcon]="canShare ? 'share' : 'copy'"
      [ngClass]="iconClass"
      class="pointer"
    ></svg>
  `,
  styleUrls: ['./clipboard-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgxSvgSpriteFragment, CfIconDirective, CdkCopyToClipboard, NgClass],
})
export class ClipboardButtonComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  @Input({ required: true }) link!: string;

  @Input() iconClass = 'tw:text-blue-50';

  customShare = input(false);
  share = output<void>();

  readonly isShare = input(true);

  isTouch!: boolean;

  constructor(
    private clipboard: Clipboard,
    private readonly responsiveService: ResponsiveService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  get canShare(): boolean {
    if (this.isShare()) {
      return false;
    }
    return !!navigator.share && navigator.canShare({ url: this.link });
  }

  ngOnInit() {
    this.responsiveService.isTouch$.subscribe((res) => {
      this.isTouch = res;
      this.cd.detectChanges();
    });
  }

  openSnackBar(message: string, action: string) {
    const id = toast.info(message, {
      duration: 5000,
      action: {
        label: action,
        onClick: () => {
          toast.dismiss(id);
        },
      },
    });
  }

  async onCopy() {
    this.clipboard.copy(this.link);
    if (this.canShare) {
      try {
        if (this.customShare()) {
          this.share.emit();
        } else {
          await navigator.share({
            url: this.link,
          });
        }
      } catch (e: unknown) {
        console.warn('Failed to share', e);
        // nothing to catch
      }
      return;
    }

    this.openSnackBar(
      this.translateService.instant('common.copied_to_clipboard'),
      this.translateService.instant('common.close'),
    );
  }
}
